import { AVATAR_COLORS, DEFAULT_AVATAR } from "@constants/global";
import Avatar from "boring-avatars";
import UserAvatar from "components/ui/v2/user-avatar-compat-v2";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import useTranslation from "next-translate/useTranslation";
import { ReactNode } from "react";
import { UserTooltip } from "components/ui/v2";
import { IUserParticipation } from "types/Question/DailyQuestionStats";
import { IUser } from "types/User.types";

interface IUserBadgeProps {
  size: number;
  user: IUser | IUserParticipation | null;
  fontSize?: number;
  showEmail?: boolean;
  previewProfile?: boolean;
  customElement?: ReactNode;
  showAvatar?: boolean;
  icon?: string | ReactNode;
  showRole?: boolean;
  profileImage?: string;
  placementProfile?: "top" | "bottom" | "left" | "right" | "auto";
  /** Nombre y customElement (p. ej. fecha) en una sola línea */
  compactAuthor?: boolean;
}

export const UserBadge = (props: IUserBadgeProps) => {
  const {
    user,
    size,
    fontSize,
    showEmail = true,
    previewProfile = false,
    customElement,
    showAvatar = true,
    icon = null,
    showRole = false,
    placementProfile = "auto",
    compactAuthor = false,
  } = props;

  const { user: userVendor } = useUserStoreContext((store) => ({
    ...store,
  }));

  const { t } = useTranslation("common");

  const tooltipSide: "top" | "bottom" =
    placementProfile === "top" ? "top" : "bottom";

  if (!user)
    return <span className="text-xs">{t("QUESTION_WITHOUT_CREATOR")}</span>;

  const authorTextClass = compactAuthor
    ? "min-w-0 truncate text-sm font-semibold leading-tight capitalize text-slate-800 dark:text-zinc-100"
    : "self-start truncate text-left capitalize text-black dark:text-white";

  const authorBlockClass = compactAuthor
    ? "flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-0"
    : "flex w-full flex-col";

  const Content = () => (
    <div className={authorBlockClass}>
      <span className={authorTextClass}>{`${user?.name} ${user?.lastname || ""}`}</span>
      {showEmail && (
        <span
          className="flex overflow-hidden text-left text-gray-600 dark:text-gray-300"
          style={{
            fontSize: fontSize || 11,
            textOverflow: "ellipsis",
            width: 200,
          }}
        >
          {user?.email || "-"}
        </span>
      )}
      {showRole && (
        <span
          className="self-start overflow-hidden text-left text-xs capitalize italic text-gray-500"
          style={{ fontSize: fontSize || 11 }}
        >
          {user?.role?.name || "-"}
        </span>
      )}
      {customElement}
    </div>
  );

  return (
    <div
      className={`flex min-w-0 items-center text-left ${showAvatar ? (compactAuthor ? "gap-1" : "gap-2") : ""}`}
    >
      <div className="relative">
        {showAvatar &&
          (user?.profileImage === DEFAULT_AVATAR ? (
            <Avatar
              size={size}
              name={`${user?.name} ${user?.lastname}`}
              variant="beam"
              colors={AVATAR_COLORS}
            />
          ) : (
            <UserAvatar user={user} size={size} />
          ))}
        {icon && (
          <span
            className="absolute h-6 w-6 items-center rounded-full border-2 border-blue-300 bg-gray-200 text-center align-middle dark:bg-gray-900"
            style={{
              bottom: -7,
              right: -4,
            }}
          >
            {icon}
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-col">
        {previewProfile ? (
          <UserTooltip user={user as Partial<IUser>} side={tooltipSide}>
            <div className={authorBlockClass}>
              <a
                href={`/profile/${user?.id}?vendorId=${userVendor?.selectedVendor.id}`}
                target="_blank"
                rel="noreferrer"
                className={compactAuthor ? authorTextClass : "self-start text-left"}
                style={
                  compactAuthor
                    ? { textTransform: "capitalize" }
                    : {
                        wordBreak: "break-word",
                        textTransform: "capitalize",
                      }
                }
              >{`${user?.name?.toLowerCase()} ${user?.lastname?.toLowerCase() || ""}`}</a>
              {showEmail && (
                <span
                  className="text-gray-600 dark:text-gray-300"
                  style={{ fontSize: fontSize || 11 }}
                >
                  {user?.email || "-"}
                </span>
              )}
              {showRole && (
                <span className="text-xs capitalize italic">
                  {user?.role?.name || "-"}
                </span>
              )}
              {customElement}
            </div>
          </UserTooltip>
        ) : (
          <Content />
        )}
      </div>
    </div>
  );
};
